import React, { useContext, useEffect } from "react";
import UserContext from "../../context/UserContext";
import UIContext from "../../context/UIContext";
import SocketContext from "../../context/SocketContext";
import { useSound } from "../../utils/sound/SoundProvider";

import LobbyRooms from "./LobbyRooms";
import LobbyPlayers from "./LobbyPlayers";
import LobbyChat from "./LobbyChat";
import featuredGamesConfig from "./featuredGamesConfig";


function Lobby({
  setShowCreateRoomModal,
  lobbyData,
  onJoinRoom,
  joinRoomError,
  setJoinRoomError,
  gameType,
  setGameType,

}) {
  const [lobbyMessages, setLobbyMessages] = React.useState(
    lobbyData.lobbyMessages
  );
  const [lobbyTypingPlayers, setLobbyTypingPlayers] = React.useState([]);

  // Context

  // Local state for UI controls

  const { gameTypes } = useContext(UIContext);
  const { user } = useContext(UserContext);
  const socket = useContext(SocketContext);
  const sound = useSound();

  // `gameType` and `setGameType` are provided by the parent `LobbyContainer`
  // so that the CreateRoom modal receives the selected game when opened.
  // const [requirePassword, setRequirePassword] = React.useState(false);
  // const [roomPassword, setRoomPassword] = React.useState("");
  const [chatInput, setChatInput] = React.useState("");
  const [blockedUsers, setBlockedUsers] = React.useState([]);
  const [playersCollapsed, setPlayersCollapsed] = React.useState(false);
  // Selected recipient for the chat input. "everyone" means public lobby message.
  const [selectedRecipient, setSelectedRecipient] = React.useState("everyone");
  // const [hideFullRooms, setHideFullRooms] = React.useState(false);
  const [allowSpectators, setAllowSpectators] = React.useState(
    typeof user?.allowSpectators === "boolean" ? user.allowSpectators : true
  );
  const [activeFeaturedIndex, setActiveFeaturedIndex] = React.useState(0);
  const [isFeaturedPaused, setIsFeaturedPaused] = React.useState(false);
  const [loadedFeaturedImages, setLoadedFeaturedImages] = React.useState({});
  const gameTypesList = Array.isArray(gameTypes)
    ? gameTypes
    : Object.values(gameTypes || {});
  const normalizeGameIdentifier = (value) => String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
  const featuredGames = featuredGamesConfig
    .map((feature) => {
      const target = normalizeGameIdentifier(feature.gameClass || feature.title);
      const matchedGame = gameTypesList.find((game) => {
        const displayName = normalizeGameIdentifier(game.displayName || game.name);
        const gameKey = normalizeGameIdentifier(game.key || game.gameClass);
        return displayName === target || gameKey === target;
      });

      const thumbnail = matchedGame?.thumbnail || null;
      const heroImage = feature.heroImage || (thumbnail
        ? thumbnail.replace(/_thumb(?=\.[^.]+$)/i, "")
        : null);

      return {
        ...feature,
        game: matchedGame || null,
        thumbnail,
        heroImage,
        gameId: matchedGame ? matchedGame.gameClass || matchedGame.key : null,
        displayTitle: matchedGame?.displayName || matchedGame?.name || feature.title,
      };
    })
    .filter((feature) => feature.gameId);

  const activeFeaturedGame = featuredGames[activeFeaturedIndex] || featuredGames[0] || null;

  // Pick a random gameType on mount (when gameTypes becomes available)
  React.useEffect(() => {
    if (gameTypes && Array.isArray(gameTypes) && gameTypes.length > 0 && setGameType) {
      const randomIdx = Math.floor(Math.random() * gameTypes.length);
      const randomGame = gameTypes[randomIdx];
      if (randomGame && randomGame.key) setGameType(randomGame.key);
    }
  }, [gameTypes, setGameType]);

  // Sync allowSpectators with user profile changes
  React.useEffect(() => {
    if (typeof user?.allowSpectators === "boolean") {
      setAllowSpectators(user.allowSpectators);
    }
  }, [user?.allowSpectators]);

  // Randomize initial featured carousel position on mount or when featuredGames changes
  React.useEffect(() => {
    if (featuredGames.length === 0) return undefined;
    setActiveFeaturedIndex(() => Math.floor(Math.random() * featuredGames.length));
    return undefined;
  }, [featuredGames.length]);

  React.useEffect(() => {
    if (featuredGames.length === 0 || isFeaturedPaused) return undefined;

    const rotation = setInterval(() => {
      setActiveFeaturedIndex((current) => (current + 1) % featuredGames.length);
    }, 5000);

    return () => clearInterval(rotation);
  }, [featuredGames.length, isFeaturedPaused]);

  React.useEffect(() => {
    if (featuredGames.length === 0) return undefined;

    let isCancelled = false;
    const imageUrls = [...new Set(featuredGames.map((feature) => feature.heroImage).filter(Boolean))];
    const preloadedImages = [];

    imageUrls.forEach((src) => {
      const markLoaded = () => {
        if (isCancelled) return;
        setLoadedFeaturedImages((current) => {
          if (current[src]) return current;
          return { ...current, [src]: true };
        });
      };

      try {
        const img = new window.Image();
        img.onload = markLoaded;
        img.onerror = markLoaded;
        img.src = src;
        if (img.complete) markLoaded();
        preloadedImages.push(img);
      } catch (e) {
        markLoaded();
      }
    });

    return () => {
      isCancelled = true;
      preloadedImages.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [featuredGames]);

  // Typing timeout ref and handlers for lobby input
  const typingTimeoutRef = React.useRef(null);
  const isActiveFeaturedImageReady = activeFeaturedGame?.heroImage
    ? !!loadedFeaturedImages[activeFeaturedGame.heroImage]
    : true;

  const handleLobbyChatInputChange = (e) => {
    e.preventDefault();
    setChatInput(e.target.value);
    // Add self to typing players and emit typing event
    onLobbyChatTyping({ username: user.username, isTyping: true });
    // Debounce: remove self from typing after 1.2s of inactivity
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      onLobbyChatTyping({ username: user.username, isTyping: false });
    }, 1200);
  };

  // Called from LobbyChat with explicit recipient (or undefined)
  const handleLobbyChatSend = (recipient) => {
    if (chatInput.trim()) {
      const message = chatInput;
      setChatInput("");
      onSendLobbyChatMessage(message, recipient);
    }
  };

  const selectFeaturedGame = (index) => {
    if (index < 0 || index >= featuredGames.length) return;
    setActiveFeaturedIndex(index);
  };

  // Lobby typing indicator: emit when user types
  const onLobbyChatTyping = ({ username: typingUser, isTyping }) => {
    if (socket && user && user.username) {
      setLobbyTypingPlayers((prev) => {
        if (isTyping) {
          if (!prev.includes(typingUser)) return [...prev, typingUser];
        } else {
          return prev.filter((u) => u !== typingUser);
        }
        return prev;
      });
      socket.emit("lobbyTyping", { username: typingUser, isTyping, context: "lobby" });
    }
  };

  // Listen for lobbyTyping events from the server to update typing players list
  React.useEffect(() => {
    if (!socket) return;
    const handleLobbyTyping = ({ username: typingUser, isTyping }) => {
      setLobbyTypingPlayers((prev) => {
        if (isTyping) {
          if (!prev.includes(typingUser)) return [...prev, typingUser];
        } else {
          return prev.filter((u) => u !== typingUser);
        }
        return prev;
      });
    };
    socket.on("lobbyTyping", handleLobbyTyping);
    return () => socket.off("lobbyTyping", handleLobbyTyping);
  }, [socket]);


  const onSendLobbyChatMessage = (message, recipient) => {
    if (socket && user && user.username) {
      try {
        // Debug: trace what we're about to send
        try { console.log("[client] onSendLobbyChatMessage - message:", message, "recipientParam:", recipient, "selectedRecipient:", selectedRecipient); } catch (e) {}
        const dest = recipient !== undefined && recipient !== null ? recipient : selectedRecipient;
        if (dest && dest !== "everyone") {
          try { console.log("[client] emitting privateMessage to:", dest); } catch (e) {}
          socket.emit("privateMessage", { toUserIds: [dest], text: message, datetime: new Date().toISOString() });
        } else {
          try { console.log("[client] emitting public sendLobbyChatMessage"); } catch (e) {}
          socket.emit("sendLobbyChatMessage", {
            username: user.username,
            text: message,
            datetime: new Date().toISOString(),
            type: "chat",
          });
        }
        // do not reset selectedRecipient here - preserve user choice
      } catch (e) {
        console.error("Failed to send lobby message", e);
      }
    }
  };

  // Block/unblock a username locally
  const handleBlockUser = (username) => {
    // Do not allow blocking yourself
    if (user && username && user.username === username) return;
    setBlockedUsers((prev) => {
      if (prev.includes(username)) return prev.filter((u) => u !== username);
      return [...prev, username];
    });
  };


  // Send private message to a single user (invoked from player menu)
  const handleSendPrivate = (targetUserId, targetUsername) => {
    if (!socket || !user) return;
    if (!targetUserId) return;
    // Prefill the recipient for the chat composer so user can type without a prompt
    setSelectedRecipient(targetUserId);
  };

  const openCreateRoomForGame = (gameId) => {
    if (!gameId) return;
    try {
      if (setGameType) setGameType(gameId);
    } catch (e) {}
    try {
      setShowCreateRoomModal(true);
    } catch (e) {}
  };

  // Lobby chat (single message)
  useEffect(() => {
    if (!socket) return;
    const lobbyChatMessageSuccess = (msg) => {
      //console.log("Lobby chat message success:", msg);
      setLobbyMessages((prev = []) => [...prev, msg]);
      try {
        sound && sound.play && sound.play("ui:chatreceived");
      } catch (e) {}
    };
    socket.on("sendLobbyChatMessageSuccess", lobbyChatMessageSuccess);
    const handlePrivateMessage = (msg) => {
      setLobbyMessages((prev = []) => [...prev, msg]);
      try { sound && sound.play && sound.play("ui:chatreceived"); } catch (e) {}
    };
    socket.on("privateMessage", handlePrivateMessage);
    return () => {
      socket.off("sendLobbyChatMessageSuccess", lobbyChatMessageSuccess);
      socket.off("privateMessage", handlePrivateMessage);
    };
  }, [socket, sound]);

  useEffect(() => {
    if (!socket) return;
    // const handleLobbyState = (roomsArr, playersArr, gameTypesArr) => {
    //   traceLog("handleLobbyState 2");
    //   setLobbyRooms(roomsArr);
    //   setLobbyPlayers(playersArr);
    //   // Optionally, you could update other state here if needed
    // };
    socket.on("changeGameTypeSuccess", ({ roomId, newGameType }) => {
      console.log("Lobby: changeGameTypeSuccess:");
    });

    socket.on("joinLobbySuccess", ({ user }) => {
      //console.log("User joined lobby:", user);
    });

  // Keep a couple of non-kick listeners; kick/vote logic removed.

    // socket.on("saveUserProfileSuccess", (updatedUser) => {
    //   traceLog("User profile updated:", updatedUser);
    // });
    return () => {
      try { socket.off('changeGameTypeSuccess'); } catch (e) {}
      try { socket.off('joinLobbySuccess'); } catch (e) {}
    };
  }, [socket]);

  // return () => {
  //   // socket.off("lobbyState", handleLobbyState);
  //   socket.off("lobbyTyping", handleLobbyTyping);

  //   socket.off("lobbyChat", handleLobbyMessage);
  // };

  // Example: pass handleLobbyTyping2 to LobbyChat if you want typing indicator

  /*
  // Find all players who are currently in rooms
  const playersInRooms = [];
  if (lobbyRooms && Array.isArray(lobbyRooms)) {
    lobbyRooms.forEach((room) => {
      if (room.players && Array.isArray(room.players)) {
        room.players.forEach((p) => {
          playersInRooms.push(p);
        });
      }
    });
  }
*/

  return (
    <>
      {/* App-level top pane */}

      {/* <div className="flex-1 bg-white dark:bg-gray-900 rounded-b shadow flex flex-col sm:flex-row gap-4 sm:gap-8 p-2 sm:p-6"> */}
      {/* Left Panel: Create Room Button + Rooms List */}

      
      <div className="flex-1 min-w-0 mb-4 sm:mb-0">
        <button
          className="bg-green-600 text-white px-4 py-2 rounded mb-4 hover:bg-green-700 w-full"
          onClick={() => {
            try {
              // If opening the modal via the generic Create button, clear any preselected game
              if (setGameType) setGameType("");
            } catch (e) {}
            setShowCreateRoomModal(true);
          }}
        >
          Create Game Room
        </button>

        {/* Rooms List below Create Room Button */}
        <LobbyRooms
          lobbyRooms={lobbyData.lobbyRooms}
          onJoinRoom={onJoinRoom}
          joinRoomError={joinRoomError}
          setJoinRoomError={setJoinRoomError}
        />

        {/* SEO content: visible descriptive content to help search engines index the lobby and available games */}
      <section className="mt-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Play Free Multiplayer Games Online</h2>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            <em><strong>Game for a Laugh</strong></em> is a free browser-based multiplayer gaming hub.</p>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Create or join game rooms to play
            casual party games with friends and family whether you're in the same physical location or not. You can even play some games solo if you just want to try them out!</p>
            <p className="mt-2 text-gray-600 dark:text-gray-300">You can play from any device with a web browser and it works seamlessly on mobiles, tablets or computers - no downloads required.
          </p>

          {activeFeaturedGame ? (
            <div className="mt-6">
              <style>{`@keyframes lobbyFeaturedHeroEnter { 0% { opacity: 0; transform: translate3d(18px, 0, 0) scale(1.02); } 100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); } }`}</style>
              <article
                role="button"
                tabIndex={0}
                onClick={() => openCreateRoomForGame(activeFeaturedGame.gameId)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openCreateRoomForGame(activeFeaturedGame.gameId);
                  }
                }}
                onMouseEnter={() => setIsFeaturedPaused(true)}
                onMouseLeave={() => setIsFeaturedPaused(false)}
                className="group relative overflow-hidden rounded-3xl border border-slate-200/80 dark:border-slate-700/80 min-h-[380px] bg-slate-950 shadow-[0_22px_70px_rgba(15,23,42,0.28)] cursor-pointer"
              >
                <div
                  key={`hero-visual-${activeFeaturedIndex}`}
                  className="absolute inset-0"
                  style={{ animation: "lobbyFeaturedHeroEnter 420ms ease-out" }}
                >
                  {activeFeaturedGame.heroImage ? (
                    <img
                      src={activeFeaturedGame.heroImage}
                      alt={activeFeaturedGame.displayTitle}
                      loading="eager"
                      fetchPriority={activeFeaturedIndex === 0 ? "high" : "auto"}
                      className={`absolute inset-0 h-full w-full object-cover transition-[transform,opacity] duration-700 ${isActiveFeaturedImageReady ? "opacity-100 group-hover:scale-105" : "opacity-0 scale-[1.02]"}`}
                    />
                  ) : null}
                  <div className={`absolute inset-0 bg-gradient-to-br ${activeFeaturedGame.accent} opacity-75 mix-blend-multiply`} />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.28),transparent_32%),linear-gradient(180deg,rgba(15,23,42,0.08)_0%,rgba(15,23,42,0.88)_72%)]" />
                </div>

                <div
                  key={`hero-copy-${activeFeaturedIndex}`}
                  className="relative flex h-full flex-col justify-between p-6 sm:p-8 text-white"
                  style={{ animation: "lobbyFeaturedHeroEnter 420ms ease-out" }}
                >
                  <div className="max-w-xl">
                    <div className="inline-flex items-center rounded-full bg-white/14 px-3 py-1 text-xs font-semibold uppercase tracking-[0.28em] text-white/88 backdrop-blur-sm">
                      {activeFeaturedGame.eyebrow}
                    </div>
                    <h3 className="mt-4 text-3xl sm:text-5xl font-black tracking-tight text-white drop-shadow-sm">
                      {activeFeaturedGame.displayTitle}
                    </h3>
                    <p className="mt-4 max-w-lg text-sm sm:text-base leading-6 text-white/86">
                      {activeFeaturedGame.desc}
                    </p>
                  </div>

                  <div className="flex flex-col gap-5">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/72">
                        <span>{String(activeFeaturedIndex + 1)}/{String(featuredGames.length)}</span>
                      </div>
                      <div className="mt-3 flex gap-2">
                        {featuredGames.map((feature, index) => (
                          <button
                            key={feature.gameId}
                            type="button"
                            aria-label={`Show ${feature.displayTitle}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              selectFeaturedGame(index);
                            }}
                            className={`h-1.5 rounded-full transition-all duration-500 ${index === activeFeaturedIndex ? "w-16 bg-white" : "w-6 bg-white/35 hover:bg-white/60"}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex pt-1">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          openCreateRoomForGame(activeFeaturedGame.gameId);
                        }}
                        className="rounded-full bg-white px-5 py-2.5 text-sm font-bold text-slate-900 shadow-sm transition-colors duration-200 hover:bg-amber-300 focus-visible:bg-amber-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
                      >
                        Create {activeFeaturedGame.displayTitle} Room
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          ) : null}

          <p className="mt-4 text-gray-600 dark:text-gray-400">
            <a
              href="#"
              className="font-medium text-sky-600 underline hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300 focus:outline-none"
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={e => {
                e.preventDefault();
                try {
                  if (setGameType) setGameType("");
                } catch (e) {}
                setShowCreateRoomModal(true);
              }}
            >
              Create a public or password protected private room
            </a>
            , and start playing instantly. Chat to others in your game via text or voice. If you want to watch others play, you can join as a spectator in supported games or just hang out in the lobby chat and wait for your friends to start a game.
          </p>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Share your game room link with friends to invite them to join your game.</p>
        </div>
      </section>
      </div>
      {/* Players List and Chat - Right Side */}
      <div className="w-full md:w-1/3 flex flex-col gap-6">
        <h2 className="text-lg">
          <button
            type="button"
            aria-expanded={!playersCollapsed}
            onClick={() => setPlayersCollapsed((s) => !s)}
            className="w-full text-left flex items-center justify-between text-gray-900 dark:text-gray-100"
          >
            <span className="flex items-center gap-2">
              <svg
                className={`w-4 h-4 transition-transform ${playersCollapsed ? "transform rotate-0" : "transform rotate-90"}`}
                viewBox="0 0 20 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path fillRule="evenodd" d="M6 4a1 1 0 011.707-.707l6 6a1 1 0 010 1.414l-6 6A1 1 0 016 16.293L11.586 11 6 5.414A1 1 0 016 4z" clipRule="evenodd" />
              </svg>
              <span>Players</span>
              <span className="text-sm text-gray-500">({(lobbyData?.lobbyPlayers || []).length})</span>
            </span>
            <span className="text-sm text-gray-500">{playersCollapsed ? "Show" : "Hide"}</span>
          </button>
        </h2>
        {!playersCollapsed && (
          <LobbyPlayers lobbyData={{ ...lobbyData, lobbyTypingPlayers }} onBlockUser={handleBlockUser} onSendPrivate={handleSendPrivate} blockedUsers={blockedUsers} />
        )}
        {/* <h2 className="text-lg flex items-center justify-between text-gray-900 dark:text-gray-100">
          Lobby Chat
        </h2>
        <LobbyChat
          lobbyMessages={lobbyMessages}
          handleLobbyChatSend={handleLobbyChatSend}
          handleLobbyChatInputChange={handleLobbyChatInputChange}
          chatInput={chatInput}
          lobbyTypingPlayers={lobbyTypingPlayers}
          onBlur={() =>
            onLobbyChatTyping({ username: user.username, isTyping: false })
          }
          lobbyPlayers={lobbyData?.lobbyPlayers || []}
          blockedUsers={blockedUsers}
          selectedRecipient={selectedRecipient}
          setSelectedRecipient={setSelectedRecipient}
        /> */}
      </div>
      {/* </div> */}
    </>
  );
}

export default Lobby;
